import { Badge } from '@/components/ui/badge';
import { Clock, AlertCircle, CheckCircle2, User } from 'lucide-react';
import SLATimer from './SLATimer';

const TicketCard = ({ ticket, onClick, showActions = false }) => {
  // Bold, high-contrast status badges (white text on saturated backgrounds)
  // — light pastel variants were unreadable on the white card surface.
  const getStatusBadge = (status) => {
    const variants = {
      'Open': 'bg-blue-600 text-white',
      'Acknowledged': 'bg-cyan-600 text-white',
      'In Progress': 'bg-amber-500 text-white',
      'Reassigned': 'bg-pink-600 text-white',
      'Resolved': 'bg-green-600 text-white',
      'Completed': 'bg-emerald-600 text-white',
      'Closed - Feedback Received': 'bg-teal-600 text-white',
      'SLA BREACHED': 'bg-red-600 text-white animate-pulse',
      'Ticket Diverted': 'bg-violet-600 text-white',
      'On Leave': 'bg-orange-500 text-white',
      'Shift Completed': 'bg-indigo-600 text-white',
      'Purchase Material Requested': 'bg-fuchsia-600 text-white',
      'Purchase Material Approved': 'bg-purple-600 text-white',
      'Purchase Material Received': 'bg-emerald-700 text-white',
      'Long Term Vacations': 'bg-rose-600 text-white',
      'Ticket Closed': 'bg-slate-700 text-white'
    };
    return (
      <Badge className={`${variants[status] || 'bg-slate-700 text-white'} font-semibold shadow-sm`}>
        {status}
      </Badge>
    );
  };

  return (
    <div 
      data-testid={`ticket-card-${ticket.ticket_id}`}
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-lg p-4 hover:border-[#E97832] hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-mono font-semibold text-sm text-slate-900">{ticket.ticket_id}</p>
          <p className="text-xs text-slate-600 mt-1">{ticket.department}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {getStatusBadge(ticket.status)}
        </div>
      </div>

      <p className="text-sm text-slate-700 mb-3 line-clamp-2">{ticket.description}</p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1">
            <User className="w-3 h-3" />
            <span>{ticket.requester_name}</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
          </div>
        </div>
        {ticket.status !== 'Resolved' && (
          <SLATimer 
            createdAt={ticket.created_at}
            breachTime={ticket.sla_breach_time}
            status={ticket.status}
            compact
          />
        )}
      </div>

      {ticket.rating && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="flex items-center gap-1 text-sm">
            <span className="text-yellow-500">★</span>
            <span className="font-semibold">{ticket.rating}/5</span>
          </div>
        </div>
      )}

      {/* Show feedback status if submitted */}
      {ticket.feedback_submitted_at && (
        <div className={`mt-3 pt-3 border-t border-slate-200 text-xs ${ticket.is_satisfactory ? 'text-green-600' : 'text-red-600'}`}>
          <div className="flex items-center gap-1">
            {ticket.is_satisfactory ? (
              <>
                <CheckCircle2 className="w-3 h-3" />
                <span>Feedback: Satisfactory</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-3 h-3" />
                <span>Feedback: Not Satisfactory</span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketCard;
